/**
 * Doctors API Service
 * Alineado con backend v2 - feature/doctor-fiscal-banking-data
 * Base URL: http://localhost:3000/api/doctors
 */

import { apiClient } from '@/lib/api/client';
import type {
  Doctor,
  CreateDoctorInput,
  UpdateDoctorInput,
  UpdateFiscalDataInput,
  UpdateBankingDataInput,
  UpdateServiceConfigInput,
  DoctorFilters,
  DoctorsListResponse,
  DoctorResponse,
} from '@/features/doctors/types/doctor.types';

/**
 * List doctors with optional filters
 * GET /api/doctors
 */
export async function getDoctors(filters?: DoctorFilters): Promise<DoctorsListResponse> {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
  if (filters?.specialtyId) params.append('specialtyId', filters.specialtyId);
  if (filters?.serviceType) params.append('serviceType', filters.serviceType);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.sortBy) params.append('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
  // Date range filters (for registration date filtering)
  if (filters?.fromDate) params.append('fromDate', filters.fromDate);
  if (filters?.toDate) params.append('toDate', filters.toDate);

  const queryString = params.toString();
  const url = queryString ? `/doctors?${queryString}` : '/doctors';

  // Debug log para verificar sorting
  console.log('[Doctors API] Request:', {
    url,
    filters,
    sortBy: filters?.sortBy,
    sortOrder: filters?.sortOrder,
  });

  return (await apiClient.get(url)) as DoctorsListResponse;
}

/**
 * Get single doctor by ID
 * GET /api/doctors/:id
 */
export async function getDoctor(id: string): Promise<Doctor> {
  const response = (await apiClient.get(`/doctors/${id}`)) as DoctorResponse;
  return response.data;
}

/**
 * Create new doctor
 * POST /api/doctors
 */
export async function createDoctor(data: CreateDoctorInput): Promise<Doctor> {
  const response = (await apiClient.post('/doctors', data)) as DoctorResponse;
  return response.data;
}

/**
 * Update doctor (full update)
 * PUT /api/doctors/:id
 */
export async function updateDoctor(id: string, data: UpdateDoctorInput): Promise<Doctor> {
  const response = (await apiClient.put(`/doctors/${id}`, data)) as DoctorResponse;
  return response.data;
}

/**
 * Delete doctor
 * DELETE /api/doctors/:id
 */
export async function deleteDoctor(id: string): Promise<void> {
  await apiClient.delete(`/doctors/${id}`);
}

/**
 * Update only fiscal data (partial update)
 * PATCH /api/doctors/:id/fiscal-data
 */
export async function updateDoctorFiscalData(
  id: string,
  data: UpdateFiscalDataInput
): Promise<Doctor> {
  const response = (await apiClient.patch(`/doctors/${id}/fiscal-data`, data)) as DoctorResponse;
  return response.data;
}

/**
 * Update only banking data (partial update)
 * PATCH /api/doctors/:id/banking-data
 */
export async function updateDoctorBankingData(
  id: string,
  data: UpdateBankingDataInput
): Promise<Doctor> {
  const response = (await apiClient.patch(`/doctors/${id}/banking-data`, data)) as DoctorResponse;
  return response.data;
}

/**
 * Update only service configuration (partial update)
 * PATCH /api/doctors/:id/service-config
 */
export async function updateDoctorServiceConfig(
  id: string,
  data: UpdateServiceConfigInput
): Promise<Doctor> {
  const response = (await apiClient.patch(`/doctors/${id}/service-config`, data)) as DoctorResponse;
  return response.data;
}

/**
 * Toggle doctor active status
 * Helper function using updateDoctor
 */
export async function toggleDoctorStatus(id: string, isActive: boolean): Promise<Doctor> {
  return updateDoctor(id, { isActive });
}

/**
 * ⭐ NEW: Check if phone number is already registered
 * GET /api/doctors/check-phone?phone=+523331234567
 * Returns { exists: boolean, message: string }
 */
export async function checkPhoneExists(
  phone: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ phone });
  const response = (await apiClient.get(`/doctors/check-phone?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if RFC is already registered
 * GET /api/doctors/check-rfc?rfc=ABCD123456XYZ
 * Returns { exists: boolean, message: string }
 */
export async function checkRfcExists(rfc: string): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ rfc: rfc.toUpperCase() });
  const response = (await apiClient.get(`/doctors/check-rfc?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if email is already registered
 * GET /api/doctors/check-email?email=doctor@example.com
 * Returns { exists: boolean, message: string }
 */
export async function checkEmailExists(
  email: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ email: email.toLowerCase() });
  const response = (await apiClient.get(`/doctors/check-email?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if bank CLABE is already registered
 * GET /api/doctors/check-bank-clabe?clabe=012345678901234567
 * Returns { exists: boolean, message: string }
 */
export async function checkBankClabeExists(
  clabe: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ clabe });
  const response = (await apiClient.get(`/doctors/check-bank-clabe?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if professional license is already registered
 * GET /api/doctors/check-professional-license?license=12345678
 * Returns { exists: boolean, message: string }
 */
export async function checkProfessionalLicenseExists(
  license: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ license });
  const response = (await apiClient.get(`/doctors/check-professional-license?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if specialty license is already registered
 * GET /api/doctors/check-specialty-license?license=12345678
 * Returns { exists: boolean, message: string }
 */
export async function checkSpecialtyLicenseExists(
  license: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ license });
  const response = (await apiClient.get(`/doctors/check-specialty-license?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * ⭐ NEW: Check if subspecialty license is already registered
 * GET /api/doctors/check-subspecialty-license?license=12345678
 * Returns { exists: boolean, message: string }
 */
export async function checkSubspecialtyLicenseExists(
  license: string
): Promise<{ exists: boolean; message: string }> {
  const params = new URLSearchParams({ license });
  const response = (await apiClient.get(`/doctors/check-subspecialty-license?${params}`)) as {
    data: { exists: boolean; message: string };
  };
  return response.data;
}

/**
 * Get decrypted billing portal password for a doctor
 * GET /api/doctors/:id/billing-portal-password
 * Security: Only call when user explicitly requests it (copy action)
 */
export async function getBillingPortalPassword(doctorId: string): Promise<{ password: string }> {
  const response = (await apiClient.get(`/doctors/${doctorId}/billing-portal-password`)) as {
    data: { password: string };
  };
  return response.data;
}

// ============================================
// DOCTOR STATS WITH AMOUNTS
// @since 2025-12-30 - Migration 0008_requirements_update
// ============================================

export interface StatusStatEntry {
  count: number;
  amount: string;
}

export interface CommissionStatusStatEntry {
  count: number;
  commission: string;
}

export interface DoctorStatsWithAmounts {
  byStatus: Record<string, StatusStatEntry>;
  byCommissionStatus: Record<string, CommissionStatusStatEntry>;
  totals: {
    totalTasks: number;
    totalAmount: string;
    totalCommission: string;
    commissionPaid: string;
    commissionPending: string;
  };
}

export interface DoctorStatsResponse {
  success: true;
  data: DoctorStatsWithAmounts;
}

/**
 * Get aggregated statistics with amounts for a doctor's tasks
 * GET /api/doctors/:id/stats
 * @since 2025-12-30 - Migration 0008_requirements_update
 */
export async function getDoctorStats(doctorId: string): Promise<DoctorStatsWithAmounts> {
  const response = (await apiClient.get(`/doctors/${doctorId}/stats`)) as DoctorStatsResponse;
  return response.data;
}
